import { Virtuoso } from 'react-virtuoso';
import type { Contact } from '@/contexts/contacts/types';
import MemberItem from '../member-Item';
import EmptyIcon from './images/ghost.svg?react';
import AddIcon from './images/add.svg?react';
import {
  flatGroupedContacts,
  groupContactsByFirstLetter,
  sortGroupedContacts,
} from './helpers';
import styles from './index.module.scss';
import type { ContactsListProps } from './interface';

const ContactsList = ({
  contacts,
  selectedContacts = [],
  className,
  onContactSelect,
  onContactClick,
  showAlphabetIndex = true,
  isPage = false,
}: ContactsListProps) => {
  const [activeLetter, setActiveLetter] = useState<string>('');

  const checkedContacts = selectedContacts;

  const groupedContacts = useMemo(
    () => sortGroupedContacts(groupContactsByFirstLetter(contacts)),
    [contacts]
  );
  const alphabet = useMemo(() => Object.keys(groupedContacts), [groupedContacts]);
  const flatContacts = useMemo(
    () => flatGroupedContacts(groupedContacts),
    [groupedContacts]
  );

  const letterIndexMap = useMemo(() => {
    const map: { [key: string]: number } = {};
    flatContacts.forEach((item, index) => {
      if (item.isGroupKey) {
        map[item.name] = index;
      }
    });
    return map;
  }, [flatContacts]);

  const handleLetterClick = (letter: string) => {
    setActiveLetter(letter);
    const rowIndex = letterIndexMap[letter];
    if (typeof rowIndex === 'number') {
      virtuosoRef.current?.scrollToIndex({ index: rowIndex, align: 'start' });
    }
  };

  const onCheck = useCallback(
    (contact: Contact) => {
      if (!onContactSelect) return;

      const newCheckedContacts = checkedContacts.some((c) => c.id === contact.id)
        ? checkedContacts.filter((c) => c.id !== contact.id)
        : [...checkedContacts, contact];

      onContactSelect(newCheckedContacts);
    },
    [checkedContacts, onContactSelect]
  );

  useLayoutEffect(() => {
    if (showAlphabetIndex) {
      setActiveLetter(alphabet[0]);
    }
  }, [alphabet, showAlphabetIndex]);

  const virtuosoRef = useRef<any>(null);

  const itemContent = useCallback(
    (index: number) => {
      const item = flatContacts[index];
      return (
        <div key={item.user_id} data-key={item.user_id}>
          {item.isGroupKey ? (
            <div
              className={cls(styles.contactGroupIndex, {
                [styles.active]: activeLetter === item.name,
              })}
            >
              {item.name}
            </div>
          ) : (
            <MemberItem
              hoverable={!!onContactSelect || !!onContactClick}
              avatarProps={{ src: item.avatar, info: item }}
              name={item.name}
              remarkName={item.remark_name}
              shortName={item.username}
              checked={checkedContacts.some((c) => c.id === item.id)}
              showCheck={!!onContactSelect}
              onClick={
                onContactSelect ? () => onCheck?.(item) : () => onContactClick?.(item)
              }
            />
          )}
        </div>
      );
    },
    [
      activeLetter,
      checkedContacts,
      flatContacts,
      onCheck,
      onContactClick,
      onContactSelect,
    ]
  );

  return (
    <div className={cls(styles.contactsList, className)}>
      <div className={styles.contactGroups}>
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: '100%' }}
          className="scrollbar_hover"
          totalCount={flatContacts.length}
          itemContent={itemContent}
          components={{
            EmptyPlaceholder: () => (
              <Flex justify="center" style={{ height: '100%', paddingTop: 90 }}>
                {isPage ? (
                  <Flex vertical align="center" gap={8}>
                    <EmptyIcon />
                    <Flex align="center" gap={6}>
                      Tab the
                      <AddIcon />
                      icon to add contacts
                    </Flex>
                  </Flex>
                ) : (
                  <Empty />
                )}
              </Flex>
            ),
          }}
        />
      </div>
      {showAlphabetIndex && (
        <div className={styles.alphabetIndex}>
          {alphabet.map((letter) => (
            <div
              key={letter}
              className={cls(styles.alphabetItem, {
                [styles.active]: activeLetter === letter,
              })}
              onClick={() => handleLetterClick(letter)}
            >
              {letter}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactsList;
